import React, {useMemo, useEffect} from 'react'
import useEffectEvent from '../../../hooks/use-effect-event'
import PropTypes from 'prop-types'
import {Accordion, Box, useAccordionItemContext} from '@chakra-ui/react3'
import Copy from '../../components/cms/copy'
import CTA from '../../components/cms/cta'
import Image from '../../components/cms/image'
import Video from '../../components/cms/video'
import {
    hasValidString,
    hasValidAsset,
    hasValidVideoContent,
    hasValidImageSrc,
    isFullVideo
} from '../../utils/utils'
import {AddIcon, RemoveIcon} from '../../components/icons'
import ButtonGroup from '../../components/cms/button-group'
import VideoControl from '../../components/cms/video-control'
import useVideoControl from '../../hooks/useVideoControl'
import {useStyles} from './context'
import RichText from '../../components/cms/rich-text'

/**
 * Pauses the video when the accordion item closes by reading the item's own
 * open state via context. Must be rendered as a direct child of Accordion.Item.
 */
const VideoPauseOnClose = ({controls, asset}) => {
    const {expanded} = useAccordionItemContext()
    const pauseIfVisible = useEffectEvent(() => {
        if (hasValidVideoContent(asset)) {
            controls.pause()
        }
    })
    useEffect(() => {
        if (!expanded) pauseIfVisible()
    }, [expanded, pauseIfVisible])
    return null
}

VideoPauseOnClose.propTypes = {
    controls: PropTypes.shape({
        pause: PropTypes.func.isRequired
    }).isRequired,
    asset: PropTypes.object
}

const AccordionItem = ({alignment, asset, cta, copy, headline, value, widths, moduleType}) => {
    const styles = useStyles()
    const videoRef = React.useRef(null)
    const isFullVideoMode = isFullVideo(asset)
    const [isVideoPlaying, onVideoToggle, controls] = useVideoControl(asset?.autoplay, videoRef, {
        isFullVideo: isFullVideoMode
    })

    const {hasAsset, hasVideo, hasImage, hasCta, hasCopy} = useMemo(
        () => ({
            hasAsset: hasValidAsset(asset),
            hasVideo: hasValidVideoContent(asset),
            hasImage: hasValidImageSrc(asset),
            hasCta: hasValidString(cta?.label),
            hasCopy: hasValidString(copy?.text)
        }),
        [asset, cta, copy]
    )

    return (
        <Accordion.Item value={value} css={styles.item}>
            {hasVideo && <VideoPauseOnClose controls={controls} asset={asset} />}
            <Accordion.ItemTrigger css={styles.trigger}>
                <RichText as="span" css={styles.itemHeadline}>
                    {headline}
                </RichText>
                <Accordion.ItemIndicator css={styles.indicator} _open={{display: 'none'}}>
                    <AddIcon />
                </Accordion.ItemIndicator>
                <Accordion.ItemIndicator css={styles.indicator} _closed={{display: 'none'}}>
                    <RemoveIcon />
                </Accordion.ItemIndicator>
            </Accordion.ItemTrigger>

            <Accordion.ItemContent css={styles.content}>
                <Box css={styles.contentInner}>
                    {/* Text content (copy and/or CTA) */}
                    {(hasCopy || hasCta) && (
                        <Box css={styles.textContent}>
                            {hasCopy && <Copy {...copy} alignment={alignment} />}
                            {hasCta && (
                                <ButtonGroup>
                                    <CTA
                                        {...cta}
                                        css={styles.cta}
                                        trackingContext={{
                                            moduleName: moduleType,
                                            eventLabel: 'primaryCta'
                                        }}
                                    />
                                </ButtonGroup>
                            )}
                        </Box>
                    )}

                    {/* Asset (image or video) */}
                    {hasAsset && (
                        <Box css={styles.assetWrapper}>
                            {hasVideo && (
                                <>
                                    <Video
                                        {...asset}
                                        ref={videoRef}
                                        widths={widths}
                                        {...(isFullVideoMode && {
                                            videoType: 'video',
                                            onPlay: controls.onPlay
                                        })}
                                    />
                                    {/* Darken layer: always visible for animation, only on poster state for full video */}
                                    {controls.overlay.showDarken && (
                                        <Box css={styles.videoDarkenLayer} />
                                    )}
                                    {/* Animation mode: custom control always visible */}
                                    {controls.overlay.showAnimationControl && (
                                        <VideoControl
                                            {...styles.videoControl}
                                            variant={
                                                asset?.showVideoInfo ? 'mediaFilled' : 'mediaGhost'
                                            }
                                            isPlaying={isVideoPlaying}
                                            onToggle={onVideoToggle}
                                        />
                                    )}
                                    {/* Full video mode: centered play button only before playing */}
                                    {controls.overlay.showPosterOverlay && (
                                        <VideoControl
                                            css={styles.fullVideoPlayButton}
                                            variant="mediaFilled"
                                            size="lg"
                                            isPlaying={false}
                                            onToggle={() => {
                                                controls.play()
                                            }}
                                        />
                                    )}
                                </>
                            )}
                            {hasImage && <Image {...asset} widths={widths} objectFit="contain" />}
                        </Box>
                    )}
                </Box>
            </Accordion.ItemContent>
        </Accordion.Item>
    )
}

AccordionItem.propTypes = {
    alignment: PropTypes.string,
    asset: PropTypes.object,
    cta: PropTypes.object,
    copy: PropTypes.object,
    headline: PropTypes.string,
    value: PropTypes.string,
    widths: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    moduleType: PropTypes.string
}

export default AccordionItem
